import type { FunctionComponent, PropsWithChildren, ReactElement } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  DatePicker,
  Form,
  FormField,
  FormRow,
  FormSection,
  FormTitle,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  Select,
  Text,
  TextArea,
  View,
} from '../ui';
import type { DeepPartial } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import type {
  AssortmentExclusion,
  CargoExclusionInput,
  ImoClass,
  OrderAttributeAssociationInput,
  OrderDetailInput,
  OrderForm_OrderFragment,
  OrderFormQuery,
  OrderInput,
  OrderRequirementAssociationInput,
} from '../../graphql';
import {
  IncoTermsType,
  LoadingUnit,
  MarginUnit,
  useOrderFormQuery,
} from '../../graphql';
import type { LocationSelectValue } from '../LocationSelect';
import LocationSelect from '../LocationSelect';
import { imoClassOptions } from '../../constants/imoClassNames';
import ReactSelect from 'react-select';
import type { WithId, WithNullableId } from '../../lib/util';
import {
  assertDefined,
  isDefined,
  isDefinedWithFields,
  mapKeyBy,
  withDefined,
} from '../../lib/util';
import {
  buildCargoExclusion,
  CargoExclusionType,
  getCargoExclusionType,
  getCargoExclusionValue,
} from './cargoExclusion';
import startCase from 'lodash/startCase';
import loadingUnitOptions, {
  loadingRateSourceToLabel,
  loadingUnitToName,
} from '../../constants/loadingUnits';
import { marginUnits } from '../../constants/margin';
import { IconArrowRightRound } from '../Icons';
import type { CargoRanges } from '../core/Capacity';
import { calculateMassAndVolumeRanges } from '../core/Capacity';
import { hToMs, msToH } from '../../lib/time';
import AutoCompleteSelect from '../AutoCompleteSelect';
import unique from 'lodash/uniq';
import { stringToNumOpt } from '../core/util';
import incoTermDescriptions, {
  asIncotermValue,
} from '../../constants/incoTermOptions';
import { zodResolver } from '@hookform/resolvers/zod';
import * as zod from 'zod';
import {
  nullableStrToEnum,
  stringToNumber,
  valueOrNull,
} from '../../lib/zod-util';
import {
  reactSelectStyles,
  restrictionsAttributesSelectStyles,
} from '../../lib/form-util';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import { useCurrentUser } from '../../state/zustand';
import { reducedUnits } from '../../constants/cargoUnits';
import LoadingSpinner from '../LoadingSpinner';
import compact from 'lodash/compact';
import { OverrideTooltip } from '../contracts/PortDataForm';
import { timeFieldFormat } from '../ui/TimeField';
import {
  getEffectiveLoadingRate,
  getLoadingTime,
  type GetEffectiveLoadingRateParams,
} from '../core/Core.loading-discharging';

export type OnSaveType = (
  data: {
    order: WithNullableId<OrderInput>;
    orderDetail: Omit<WithNullableId<OrderDetailInput>, 'orderId'>;
    cargoExclusions: Omit<CargoExclusionInput, 'orderId'>[];
    previousExclusions?: Omit<WithId<CargoExclusionInput>, 'orderId'>[];
    specialRequirements: Omit<OrderRequirementAssociationInput, 'orderId'>[];
    previousSpecialRequirements: Omit<
      WithId<OrderRequirementAssociationInput>,
      'orderId'
    >[];
    orderAttributes: Omit<OrderAttributeAssociationInput, 'orderId'>[];
    previousOrderAttributes: Omit<
      WithId<OrderAttributeAssociationInput>,
      'orderId'
    >[];
  },
  opts?: { shouldClose?: boolean; createAnother?: boolean },
) => Promise<Error[]>;

export const UPCARET = <>&#9650;</>;
export const DOWNCARET = <>&#9660;</>;

export function formatLoadingRateOverrideTooltip(
  params: GetEffectiveLoadingRateParams,
) {
  const rate =
    params.voyageCharter?.rate && params.voyageCharter.unit
      ? { ...params.voyageCharter, source: 'Laytime terms' }
      : { ...params.coa, source: 'COA' };

  return `Current value overrides value defined on ${rate?.source}. Value on ${rate?.source}: ${rate?.rate} ${loadingUnitToName[rate?.unit ?? 'METRIC_TONNES_PER_DAY']}`;
}

export type InitialOrderValues = DeepPartial<OrderForm_OrderFragment>;

interface OrderFormProps {
  readOnly?: boolean;
  isLoading?: boolean;
  onSave: OnSaveType;
  onCancel?: () => void;
  onDelete?: () => void;
  isAssigned?: boolean;
  isPublished?: boolean;
  order?: OrderForm_OrderFragment | undefined;
  initialValues?: InitialOrderValues;
}

export const valueAndLabel = zod.object({
  value: zod.number(),
  label: zod.string(),
});

const saveSchema = zod.object({
  assortmentId: zod.number(),
  cargoAmount: zod.number(),
  cargoUnitId: zod.number().nullable(),
  externalOrderId: zod.string().nullable(),
  fixtureNote: zod.string().nullable(),
  imoClass: zod.string().nullable(),
  orderName: zod.string().nullable(),
  extraInfo: zod.string().nullable(),
  chartererOrganizationId: zod.number().nullable(),
  senderOrganisationId: zod.number().nullable(),
  receiverOrganisationId: zod.number().nullable(),
  supplyContractId: zod.number().nullable(),
  incoTerms: nullableStrToEnum(IncoTermsType),
  loadingRate: zod.number().nullable(),
  loadingUnit: zod.nativeEnum(LoadingUnit).nullable(),
  dischargingRate: zod.number().nullable(),
  dischargingUnit: zod.nativeEnum(LoadingUnit).nullable(),
  loadingOverheadDuration: zod.number().nullable(),
  dischargingOverheadDuration: zod.number().nullable(),
  cargoExclusions: zod.array(valueAndLabel),
  cargoExclusionAmount: zod.number(),
  cargoExclusionType: zod.nativeEnum(CargoExclusionType),
  specialRequirements: zod.array(valueAndLabel),
  orderAttributes: zod.array(valueAndLabel),
  stowageFactor: zod
    .object({
      min: zod.number().nullable(),
      max: zod.number().nullable(),
    })
    .optional(),
  specificGravity: zod.number().nullable().optional(),
  dischargePort: valueAndLabel,
  loadPort: valueAndLabel,
  marginAmount: zod.number().nullable(),
  marginUnit: zod.nativeEnum(MarginUnit).optional(),
  closingDate: zod.date().nullable(),
  layday: zod.date().nullable(),
  cancelling: zod.date().nullable(),
  rta: zod.date().nullable(),
  deliveredBefore: zod.date().nullable(),
});

const orderFormSchema = saveSchema.extend({
  cargoExclusions: zod.array(
    zod.object({
      value: zod.number().nullable(),
      label: zod.string().optional(),
    }),
  ),
});

type OrderFormFields = zod.infer<typeof orderFormSchema>;

const OrderForm = (props: OrderFormProps): ReactElement => {
  const currentUser = useCurrentUser();
  assertDefined(currentUser, 'User should be logged in');
  const dataq = useOrderFormQuery({});
  const { data } = dataq;

  if (!data) {
    return <div />;
  }

  return <OrderFormInner {...props} data={data} />;
};

const MissingPortCoordinatesError = ({
  order,
}: {
  order: OrderForm_OrderFragment | undefined;
}) => {
  const missingPortCoordinates = useMemo(() => {
    const portNames = new Set();
    const loadPort = order?.locationByLoadPortId;
    const dischargingPort = order?.locationByDischargePortId;
    if ((!loadPort?.longitude || !loadPort.longitude) && loadPort?.name) {
      portNames.add(loadPort.name);
    }
    if (
      (!dischargingPort?.longitude || !dischargingPort.longitude) &&
      dischargingPort?.name
    ) {
      portNames.add(dischargingPort.name);
    }

    return [...portNames];
  }, [order]);

  return (
    <>
      {missingPortCoordinates.length ? (
        <Text marginBottom={10} color="#9d0300">
          Unable to find coordinates for port
          {missingPortCoordinates.length === 1 ? ' ' : 's '}
          <strong>{missingPortCoordinates.join(', ')}</strong>. Please check
          Admin - Ports to add missing coordinates.
        </Text>
      ) : null}
    </>
  );
};
const InvalidDataErrors = ({
  order,
}: {
  order: OrderForm_OrderFragment | undefined;
}) => {
  const { invalidDates, missingCoa } = useMemo(() => {
    const initialData = {
      missingCoa: null,
      invalidDates: [],
    };
    const invalidDates = new Set();
    const rawNotices = order?.apiOrdersNoticesByOrderId?.nodes[0]?.notices;
    if (!rawNotices) return initialData;
    const notices = JSON.parse(rawNotices);
    if (!notices) return initialData;

    const missingCoa = notices?.missing_coa_contract;
    for (const notice of notices?.invalid_dates ?? []) {
      invalidDates.add(
        notice
          .split('_')
          .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' '),
      );
    }
    return {
      missingCoa,
      invalidDates: [...invalidDates],
    };
  }, [order]);

  return (
    <>
      {invalidDates.length ? (
        <Text marginBottom={10} color="#9d0300">
          Received invalid date{invalidDates.length === 1 ? ' ' : 's '} from
          integration for <strong>{invalidDates.join(', ')}</strong>. Please
          check the dates from integration side.
        </Text>
      ) : null}
      {missingCoa ? (
        <Text marginBottom={10} color="#9d0300">
          Missing COA contract for this order. Please check the integration.
        </Text>
      ) : null}
    </>
  );
};

export const CalculationBox: FunctionComponent<
  PropsWithChildren<{ minWidth?: string | number }>
> = (props) => (
  <Box
    style={{
      display: 'flex',
      minHeight: '40px',
      minWidth: props.minWidth || '410px',
      marginRight: '5px',
      marginBottom: '10px',
      padding: '10px',
      alignItems: 'center',
      flex: '0 0 auto',
      borderStyle: 'dashed',
      borderWidth: '1px',
      borderColor: 'rgba(0, 0, 0, 0.41)',
    }}
  >
    <Box style={{ marginRight: '1em', alignSelf: 'center' }}>
      <IconArrowRightRound />
    </Box>
    {props.children}
  </Box>
);

type OrderFormOrg = NonNullable<
  NonNullable<OrderFormQuery['allOrganizations']>['nodes']
>[0];

export const cargoExclusionTypeOptions: {
  id: CargoExclusionType;
  label: string;
}[] = [
  {
    id: CargoExclusionType.distance,
    label: 'Distance',
  },
  {
    id: CargoExclusionType.tanks,
    label: 'Tanks',
  },
  {
    id: CargoExclusionType.voyages,
    label: 'Voyages',
  },
];

const OrderFormInner = ({
  readOnly,
  isLoading,
  onSave,
  onCancel,
  onDelete,
  isAssigned,
  isPublished,
  order,
  data,
  initialValues,
}: OrderFormProps & {
  data: OrderFormQuery;
}): ReactElement => {
  // noinspection SuspiciousTypeOfGuard
  const isEdit = order && typeof order.id === 'number';

  const initData = order ?? initialValues;

  const currentUser = useCurrentUser();

  const cannotChangePortReason: undefined | string = isPublished
    ? `You cannot change ports on a published order.`
    : undefined;

  const assortmentOptions = useMemo(
    () =>
      data.allAssortments?.nodes
        .map((assortment) => ({
          value: assortment?.id ?? -1,
          label: assortment?.name ?? '',
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) ?? [],
    [data],
  );

  const cargoExclusionOptions = useMemo<
    Array<{ value: number; label: string }>
  >(
    () =>
      data.allAssortmentGroups?.nodes.map((ag) => ({
        value: ag?.id ?? NaN,
        label: ag?.name ?? 'UNNAMED CARGO EXCLUSION',
      })) ?? [],
    [data],
  );

  const shouldUseReducedUnits =
    currentUser?.rootOrganization?.configReducedUnits ?? false;
  const cargoUnitOptions = useMemo(
    () =>
      data.allCargoUnits?.nodes
        .filter((unit) => {
          if (shouldUseReducedUnits) {
            return reducedUnits.includes(unit?.name ?? '');
          }
          return unit;
        })
        .map((cargoUnit) => ({
          id: cargoUnit?.id ?? -1,
          label: cargoUnit?.name ?? '',
        })) ?? [],
    [data, shouldUseReducedUnits],
  );

  const specialRequirementOptions = useMemo(
    () =>
      data.allSpecialRequirements?.nodes.map((sr) => ({
        value: sr?.id ?? -1,
        label: sr?.name ?? '',
      })) ?? [],
    [data],
  );

  const orderAttributeOptions = useMemo(
    () =>
      data.allOrderAttributes?.nodes.map((sr) => ({
        value: sr?.id ?? -1,
        label: sr?.name ?? '',
      })) ?? [],
    [data],
  );

  const chartererOrganizationOptions = useMemo(() => {
    const tree = new Map<number, OrderFormOrg[]>();
    data.allOrganizations?.nodes.forEach((org) => {
      if (!org) return;
      const parentId = org.parentOrganizationId;
      if (parentId) {
        const children = tree.get(parentId) || [];
        if (!children.length) {
          tree.set(parentId, children);
        }
        children.push(org);
      }
    });

    const chartererChildOrganizations = [];
    const userOrgId = currentUser?.organizationId ?? null;
    const userOrg: OrderFormOrg | undefined = data.allOrganizations?.nodes.find(
      (org) => org && org.id === userOrgId,
    );
    const queue: OrderFormOrg[] = userOrg ? [userOrg] : [];
    while (queue.length) {
      const current = queue.pop();
      if (!current) continue;
      if (current.charterer) {
        chartererChildOrganizations.push(current);
      }
      const children = tree.get(current.id);
      if (children) {
        queue.push(...children);
      }
    }

    const res =
      chartererChildOrganizations.map((org) => ({
        value: org.id,
        label: org.name ?? 'UNNAMED ORGANIZATION',
      })) ?? [];

    // Add user's own org as a backwards compatibility and in case we're a root org user witn no charterers yet
    if (userOrg && userOrgId && !res.find((r) => r.value === userOrgId)) {
      res.push({
        value: userOrgId,
        label: userOrg.name ?? 'UNNAMED ORGANIZATION',
      });
    }

    res.sort((a, b) => a.label.localeCompare(b.label));

    return [
      {
        value: null,
        label: 'None',
      },
      ...res,
    ];
  }, [currentUser?.organizationId, data.allOrganizations?.nodes]);

  const senderOrganizationOptions = useMemo(() => {
    const res =
      data.allOrganizations?.nodes
        .filter(isDefined)
        .filter((o) => o.sender)
        .map((org) => ({
          value: org.id ?? -1,
          label: org.name ?? 'UNNAMED ORGANIZATION',
        })) ?? [];

    const orgId = currentUser?.organizationId ?? null;
    const userOrg = orgId
      ? data.allOrganizations?.nodes.find((o) => o?.id === orgId)
      : null;
    if (res.length === 0 && userOrg) {
      res.push({
        value: userOrg.id,
        label: userOrg.name ?? 'UNNAMED ORGANIZATION',
      });
    }

    res.sort((a, b) => a.label.localeCompare(b.label));

    return [
      {
        value: null,
        label: 'None',
      },
      ...res,
    ];
  }, [currentUser?.organizationId, data.allOrganizations?.nodes]);

  const receiverOrganizationOptions = useMemo(() => {
    const res =
      data.allOrganizations?.nodes
        .filter(isDefined)
        .filter((o) => o.receiver)
        .map((org) => ({
          value: org.id ?? -1,
          label: org.name ?? 'UNNAMED ORGANIZATION',
        })) ?? [];
    res.sort((a, b) => a.label.localeCompare(b.label));
    return [
      {
        value: null,
        label: 'None',
      },
      ...res,
    ];
  }, [data]);

  const supplyContractOptions = useMemo(() => {
    const res =
      data.allSupplyContracts?.nodes.map((sc) => ({
        value: sc?.id ?? -1,
        label: sc?.supplyContract ?? `UNNAMED SUPPLY CONTRACT ${sc?.id}`,
      })) ?? [];

    res.sort((a, b) => a.label.localeCompare(b.label));

    return [
      {
        value: null,
        label: 'None',
      },
      ...res,
    ];
  }, [data]);

  const incoTermOptions = useMemo(() => {
    const res = incoTermDescriptions.map((o) => ({
      value: o.name,
      label: o.description,
    }));

    res.sort((a, b) => a.label.localeCompare(b.label));

    return [
      {
        value: null,
        label: 'None',
      },
      ...res,
    ];
  }, []);

  const firstCargoExclusion = initData?.cargoExclusionsByOrderId?.nodes?.[0];

  const orderDetail = initData?.orderDetailsByOrderId?.nodes?.[0];

  const cargoExclusionOptionsByValue = mapKeyBy(
    cargoExclusionOptions,
    (opt) => opt.value,
  );

  const orderName = orderDetail?.orderName ?? null;

  const DEFAULT_LAYCAN_DAYS = 3;

  const defaultValues = useMemo(
    (): Partial<OrderFormFields> => ({
      assortmentId: initData?.assortmentId ?? undefined,
      cargoAmount: stringToNumOpt(initData?.cargoAmount) ?? undefined,
      cargoExclusions: initData?.cargoExclusionsByOrderId?.nodes
        ?.map((c) => c?.assortmentGroup)
        .filter(isDefined)
        .map((cid) => cargoExclusionOptionsByValue.get(cid))
        .filter(isDefined),
      specialRequirements:
        initData?.orderRequirementAssociationsByOrderId?.nodes
          ?.map((n) =>
            specialRequirementOptions.find(
              (sr) => sr.value === n?.specialRequirementId,
            ),
          )
          .filter(isDefined) ?? [],
      orderAttributes:
        initData?.orderAttributeAssociationsByOrderId?.nodes
          ?.map((n) =>
            orderAttributeOptions.find(
              (sr) => sr.value === n?.orderAttributeId,
            ),
          )
          .filter(isDefined) ?? [],
      cargoExclusionAmount:
        (firstCargoExclusion && getCargoExclusionValue(firstCargoExclusion)) ??
        0,
      cargoExclusionType:
        (firstCargoExclusion && getCargoExclusionType(firstCargoExclusion)) ??
        CargoExclusionType.voyages,
      cargoUnitId: initData?.cargoUnitId,
      orderName,
      extraInfo: orderDetail?.extraInfo,
      externalOrderId: initData?.externalOrderId ?? null,
      fixtureNote: orderDetail?.fixtureNote ?? null,
      chartererOrganizationId: initData?.chartererOrganizationId ?? null,
      receiverOrganisationId: orderDetail?.receiverOrganisationId,
      senderOrganisationId: orderDetail?.senderOrganisationId ?? undefined,
      supplyContractId: orderDetail?.supplyContractId ?? null,
      imoClass: orderDetail?.imoClass ?? null,
      incoTerms: orderDetail?.incoTerms ?? null,
      loadingRate: stringToNumOpt(initData?.loadingRate),
      loadingUnit: initData?.loadingUnit ?? undefined,
      dischargingRate: stringToNumOpt(initData?.dischargingRate),
      dischargingUnit: initData?.dischargingUnit ?? undefined,
      loadingOverheadDuration: initData?.loadingOverheadDuration
        ? stringToNumOpt(
            msToH(+Number(initData.loadingOverheadDuration)).toFixed(2),
          )
        : null,
      dischargingOverheadDuration: initData?.dischargingOverheadDuration
        ? stringToNumOpt(
            msToH(+Number(initData.dischargingOverheadDuration)).toFixed(2),
          )
        : null,
      stowageFactor:
        initData?.stowageFactorMin || initData?.stowageFactorMax
          ? {
              min: stringToNumOpt(initData.stowageFactorMin) ?? null,
              max: stringToNumOpt(initData.stowageFactorMax) ?? null,
            }
          : undefined,
      specificGravity:
        withDefined(initData?.specificGravity, (x) => +x) ?? null,
      dischargePort: [
        {
          label: initData?.locationByDischargePortId?.name,
          value: initData?.locationByDischargePortId?.id,
        },
      ].filter(isDefinedWithFields)[0],
      loadPort: [
        {
          label: initData?.locationByLoadPortId?.name,
          value: initData?.locationByLoadPortId?.id,
        },
      ].filter(isDefinedWithFields)[0],
      marginAmount: stringToNumOpt(initData?.marginAmount),
      marginUnit: initData?.marginUnit ?? MarginUnit.PERCENTAGE,
      closingDate: orderDetail?.closingDate
        ? new Date(orderDetail?.closingDate)
        : undefined,
      rta: initData?.rta ? new Date(initData.rta) : undefined,
      cancelling: initData?.cancelling
        ? new Date(initData.cancelling)
        : undefined,
      layday: initData?.layday ? new Date(initData.layday) : undefined,
      deliveredBefore: initData?.deliveredBefore
        ? new Date(initData.deliveredBefore)
        : undefined,
    }),
    [
      firstCargoExclusion,
      initData,
      orderDetail?.closingDate,
      orderName,
      orderDetail?.fixtureNote,
      orderDetail?.extraInfo,
      orderDetail?.imoClass,
      orderDetail?.incoTerms,
      orderDetail?.receiverOrganisationId,
      orderDetail?.senderOrganisationId,
      orderDetail?.supplyContractId,
      specialRequirementOptions,
      orderAttributeOptions,
      cargoExclusionOptionsByValue,
    ],
  );

  const uf = useForm<Partial<OrderFormFields>>({
    mode: 'onBlur',
    resolver: zodResolver(orderFormSchema),
    defaultValues,
  });

  const [isSettingsOpen, setSettingsOpen] = useState(false);
  const [isConfirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmChangePort, setConfirmChangePort] = useState<null | {
    description: string;
    handler: () => void;
  }>(null);
  const [dischargePortTimezone, setDischargePortTimezone] = useState<
    string | undefined
  >(order?.locationByDischargePortId?.timezone ?? undefined);
  const [loadPortTimezone, setLoadPortTimezone] = useState<string | undefined>(
    order?.locationByLoadPortId?.timezone ?? undefined,
  );

  const getAssortmentExclusionsForAssortmentGroup = (
    groupId: number,
  ): Array<Omit<AssortmentExclusion, 'nodeId'>> => {
    if (!data.allAssortmentGroups) {
      return [];
    }

    const g = data.allAssortmentGroups.nodes.find((g) => g?.id == groupId);
    if (!g) {
      return [];
    }

    return (
      g.assortmentExclusionsByNextAssortmentGroup.nodes.filter(isDefined) ?? []
    );
  };

  const getAssortmentById = useCallback(
    (id: number | undefined) =>
      isDefined(id)
        ? (data.allAssortments?.nodes.find((a) => a?.id === id) ?? null)
        : null,
    [data.allAssortments?.nodes],
  );

  const getInitialExclusionValues = (
    assortmentId: number,
  ): {
    cargoExclusions: typeof cargoExclusionOptions;
    cargoExclusionAmount: number;
    cargoExclusionType: CargoExclusionType;
  } => {
    const assortment = getAssortmentById(assortmentId);

    if (!assortment || assortment.assortmentGroup == undefined) {
      return {
        cargoExclusionAmount: 0,
        cargoExclusionType: CargoExclusionType.voyages,
        cargoExclusions: [],
      };
    }

    const reqs = getAssortmentExclusionsForAssortmentGroup(
      assortment.assortmentGroup,
    );
    if (reqs.length === 0) {
      return {
        cargoExclusionAmount: 0,
        cargoExclusionType: CargoExclusionType.voyages,
        cargoExclusions: [],
      };
    }
    const excludedGroupIds = reqs
      .map((r) => r.previousAssortmentGroup)
      .filter(isDefined);

    const maxValue = Math.max(0, ...reqs.map((r) => r.voyages ?? 0));
    return {
      cargoExclusions: cargoExclusionOptions.filter((opt) =>
        withDefined(opt.value, excludedGroupIds.includes),
      ),
      cargoExclusionAmount: maxValue,
      cargoExclusionType: CargoExclusionType.voyages,
    };
  };

  const configEnableDefaultLaycan = useMemo(() => {
    const orgId = currentUser?.organizationId ?? null;
    return orgId
      ? data.allOrganizations?.nodes?.find((org) => org?.id === orgId)
          ?.configEnableDefaultLaycan
      : undefined;
  }, [data.allOrganizations?.nodes, currentUser?.organizationId]);

  const {
    control,
    formState: { errors },
    register,
    handleSubmit,
    watch,
    reset,
    getValues,
    setValue,
    setError,
  } = uf;

  useEffect(() => {
    // Initialize laycan when timezone is known and default laycan feature is enabled
    if (loadPortTimezone && configEnableDefaultLaycan) {
      const values = getValues();
      let { layday, cancelling } = values;
      if (!layday && !isEdit) {
        layday = moment.tz(loadPortTimezone).startOf('day').toDate();
      }
      if (!cancelling && !isEdit) {
        cancelling = moment(layday)
          .tz(loadPortTimezone)
          .add(2, 'days')
          .endOf('day')
          .toDate();
      }
      if (layday !== values.layday) {
        setValue('layday', layday);
      }
      if (cancelling !== values.cancelling) {
        setValue('cancelling', cancelling);
      }
    }
  }, [
    isEdit,
    configEnableDefaultLaycan,
    getValues,
    loadPortTimezone,
    setValue,
  ]);

  useEffect(() => {
    return watch(({ layday, cancelling }, info) => {
      // When setting layday or cancelling, the other one defaults to offset by DEFAULT_LAYCAN_DAYS when unset of invalid
      if (info.type !== 'change' || !loadPortTimezone) return;
      if (info.name === 'layday' && layday) {
        setValue(
          'cancelling',
          moment(layday)
            .tz(loadPortTimezone)
            .add(DEFAULT_LAYCAN_DAYS - 1, 'days')
            .endOf('day')
            .toDate(),
        );
      } else if (
        info.name === 'cancelling' &&
        cancelling &&
        (!layday || !moment(cancelling).isAfter(moment(layday)))
      ) {
        setValue(
          'layday',
          moment(cancelling)
            .tz(loadPortTimezone)
            .subtract(DEFAULT_LAYCAN_DAYS - 1, 'days')
            .startOf('day')
            .toDate(),
        );
      }
    }).unsubscribe;
  }, [loadPortTimezone, setValue, watch]);

  const errorKeys = Object.keys(errors);

  const errorMessages = errorKeys.map((key) => {
    const explanation =
      key === 'orderName'
        ? ` ${errors[key as keyof typeof errors]?.message ?? ''}`
        : '';
    return `${startCase(key)} is not valid.${explanation}`;
  });

  const currentExclusions = watch('cargoExclusions');

  const aid = watch('assortmentId');

  const assortmentId: number | undefined =
    withDefined(aid, (x: string | number) => Number(x)) ?? undefined; // The form seems to set a string value into the id from somewhere, sometimes
  const assortment = useMemo(
    () => getAssortmentById(assortmentId),
    [getAssortmentById, assortmentId],
  );

  const cargoUnit = data.allCargoUnits?.nodes
    .filter((unit) => {
      if (shouldUseReducedUnits) {
        return reducedUnits.includes(unit?.name ?? '');
      }
      return unit;
    })
    .find(
      (cu) => cu?.id === withDefined(watch('cargoUnitId'), (x) => Number(x)),
    );
  const cargoAmount = withDefined(watch('cargoAmount'), Number);
  const marginAmount = withDefined(watch('marginAmount'), Number);
  const marginUnit = watch('marginUnit');
  const specificGravity = withDefined(watch('specificGravity'), Number);
  const stowageFactorMin = withDefined(watch('stowageFactor.min'), Number);
  const stowageFactorMax = withDefined(watch('stowageFactor.max'), Number);
  const loadPortId = withDefined(watch('loadPort')?.value, Number);
  const dischargePortId = withDefined(watch('dischargePort')?.value, Number);
  const loadingRate = withDefined(watch('loadingRate'), Number);
  const dischargingRate = withDefined(watch('dischargingRate'), Number);
  const loadingUnit = watch('loadingUnit');
  const dischargingUnit = watch('dischargingUnit');
  const supplyContractId = withDefined(watch('supplyContractId'), Number);
  const supplyContract =
    supplyContractId !== null
      ? data.allSupplyContracts?.nodes.find((sc) => sc?.id === supplyContractId)
      : null;
  const generatedName =
    supplyContract && supplyContractId !== orderDetail?.supplyContractId
      ? `${supplyContract?.batchCode}${supplyContract?.year}${supplyContract.batchSeq}`
      : orderName;

  const watchedLayday = watch('layday');
  const watchedCancelling = watch('cancelling');

  useEffect(() => {
    setValue('orderName', generatedName ?? null);
  }, [generatedName, setValue]);

  let cargoRanges: CargoRanges = {
    mass: { min: 0, max: 0 },
    volume: { min: 0, max: 0 },
  };

  if (cargoUnit) {
    cargoRanges = calculateMassAndVolumeRanges(
      {
        assortmentByAssortmentId: assortment,
        cargoUnitByCargoUnitId: cargoUnit,
        cargoAmount,
        marginAmount,
        marginUnit,
        specificGravity,
        stowageFactorMin,
        stowageFactorMax,
      },
      false,
    );
  }

  const {
    coaPortDataByPortId,
    voyageCharterCancellingMs,
    voyageCharterLaydayMs,
    voyageCharterPortDataByPortId,
  } = useMemo(() => {
    const incomingVoyageCharter =
      order?.orderVoyageCharterRelationsByOrderId.nodes.find(
        (rel) => rel?.outgoing === false,
      )?.voyageCharterByVoyageCharterId;

    const coa =
      incomingVoyageCharter?.voyageCharterToContractsByVoyageCharterId.nodes?.at(
        0,
      )?.coaContractByCoaId;

    const voyageCharterPortDataByPortId = mapKeyBy(
      compact(
        incomingVoyageCharter?.voyageCharterPortDataByVoyageCharterId.nodes.flatMap(
          (node) => node?.portDatumByPortDataId,
        ),
      ),
      (portDatum) => portDatum.portId,
    );

    const coaPortDataByPortId = mapKeyBy(
      compact(
        coa?.coaContractPortDataByCoaContractId.nodes.flatMap(
          (node) => node?.portDatumByPortDataId,
        ),
      ),
      (portDatum) => portDatum.portId,
    );

    return {
      coaPortDataByPortId,
      voyageCharterCancellingMs: incomingVoyageCharter?.cancelling
        ? new Date(incomingVoyageCharter.cancelling).valueOf()
        : null,
      voyageCharterLaydayMs: incomingVoyageCharter?.layday
        ? new Date(incomingVoyageCharter.layday).valueOf()
        : null,
      voyageCharterPortDataByPortId,
    };
  }, [order?.orderVoyageCharterRelationsByOrderId.nodes]);

  const hasLaydayOverride =
    !!watchedLayday &&
    !!voyageCharterLaydayMs &&
    watchedLayday.valueOf() !== voyageCharterLaydayMs;
  const hasCancellingOverride =
    !!watchedCancelling &&
    !!voyageCharterCancellingMs &&
    watchedCancelling.valueOf() !== voyageCharterCancellingMs;

  const voyageCharterLoadPortData = loadPortId
    ? voyageCharterPortDataByPortId.get(loadPortId)
    : null;
  const coaLoadPortData = loadPortId
    ? coaPortDataByPortId.get(loadPortId)
    : null;

  const voyageCharterDischargePortData = dischargePortId
    ? voyageCharterPortDataByPortId.get(dischargePortId)
    : null;
  const coaDischargePortData = dischargePortId
    ? coaPortDataByPortId.get(dischargePortId)
    : null;

  const effectiveLoadingRateParams = {
    order: {
      rate: loadingRate ?? null,
      unit: loadingUnit ?? null,
    },
    assortment: {
      rate: withDefined(assortment?.loadingRate, Number),
      unit: assortment?.loadingUnit ?? null,
    },
    coa: {
      rate: withDefined(coaLoadPortData?.loadingRate, Number),
      unit: coaLoadPortData?.loadingDischargeUnit ?? null,
    },
    port: null,
    vessel: null,
    voyageCharter: {
      rate: withDefined(voyageCharterLoadPortData?.loadingRate, Number),
      unit: voyageCharterLoadPortData?.loadingDischargeUnit ?? null,
    },
    portCommodity: null,
  };

  const {
    rate: effectiveLoadingRate,
    unit: effectiveLoadingUnit,
    source: effectiveLoadingRateSource,
  } = getEffectiveLoadingRate(effectiveLoadingRateParams);

  const minLoadingTime = getLoadingTime(
    cargoRanges.mass.min,
    cargoRanges.volume.min,
    effectiveLoadingRate ?? Infinity,
    effectiveLoadingUnit,
  );

  const maxLoadingTime = getLoadingTime(
    cargoRanges.mass.max,
    cargoRanges.volume.max,
    effectiveLoadingRate ?? Infinity,
    effectiveLoadingUnit,
  );

  const effectiveDischargeRateParams = {
    order: {
      rate: dischargingRate ?? null,
      unit: dischargingUnit ?? null,
    },
    assortment: {
      rate: withDefined(assortment?.dischargingRate, Number),
      unit: assortment?.dischargingUnit ?? null,
    },
    coa: {
      rate: withDefined(coaDischargePortData?.dischargeRate, Number),
      unit: coaDischargePortData?.loadingDischargeUnit ?? null,
    },
    port: null,
    vessel: null,
    voyageCharter: {
      rate: withDefined(voyageCharterDischargePortData?.dischargeRate, Number),
      unit: voyageCharterDischargePortData?.loadingDischargeUnit ?? null,
    },
    portCommodity: null,
  };

  const {
    rate: effectiveDischargeRate,
    unit: effectiveDischargeUnit,
    source: effectiveDischargeRateSource,
  } = getEffectiveLoadingRate(effectiveDischargeRateParams);

  const minDischargingTime = getLoadingTime(
    cargoRanges.mass.min,
    cargoRanges.volume.min,
    effectiveDischargeRate ?? Infinity,
    effectiveDischargeUnit,
  );

  const maxDischargingTime = getLoadingTime(
    cargoRanges.mass.max,
    cargoRanges.volume.max,
    effectiveDischargeRate ?? Infinity,
    effectiveDischargeUnit,
  );

  const contractLoadingRate =
    effectiveLoadingRateParams.voyageCharter?.rate &&
    effectiveLoadingRateParams.voyageCharter.unit
      ? effectiveLoadingRateParams.voyageCharter
      : effectiveLoadingRateParams.coa?.rate &&
          effectiveLoadingRateParams.coa.unit
        ? effectiveLoadingRateParams.coa
        : null;

  const hasLoadingRateOverride =
    effectiveLoadingRateSource === 'order' &&
    contractLoadingRate &&
    (effectiveLoadingRate !== contractLoadingRate.rate ||
      effectiveLoadingUnit !== contractLoadingRate.unit);

  const contractDischargeRate =
    effectiveDischargeRateParams.voyageCharter?.rate &&
    effectiveDischargeRateParams.voyageCharter.unit
      ? effectiveDischargeRateParams.voyageCharter
      : effectiveDischargeRateParams.coa?.rate &&
          effectiveDischargeRateParams.coa.unit
        ? effectiveDischargeRateParams.coa
        : null;

  const hasDischargeRateOverride =
    effectiveDischargeRateSource === 'order' &&
    contractDischargeRate &&
    (effectiveDischargeRate !== contractDischargeRate.rate ||
      effectiveDischargeUnit !== contractDischargeRate.unit);

  const loadingRatePlaceholder = effectiveLoadingRate
    ? `${effectiveLoadingRate} ${loadingUnitToName[effectiveLoadingUnit]} (${loadingRateSourceToLabel[effectiveLoadingRateSource]})`
    : undefined;

  const dischargeRatePlaceholder = effectiveDischargeRate
    ? `${effectiveDischargeRate} ${loadingUnitToName[effectiveDischargeUnit]} (${loadingRateSourceToLabel[effectiveDischargeRateSource]})`
    : undefined;

  const onSubmit =
    (createAnother: boolean, shouldClose: boolean) =>
    async (formData: OrderFormFields) => {
      const data = saveSchema.parse(formData);

      const {
        dischargePort,
        loadPort,
        layday,
        cancelling,
        rta,
        deliveredBefore,
        stowageFactor,
        specificGravity,
        loadingRate,
        loadingUnit,
        dischargingRate,
        dischargingUnit,
        assortmentId,
        cargoAmount,
        cargoUnitId,
        externalOrderId,
        marginAmount,
        marginUnit,
        orderName,
        fixtureNote,
        extraInfo,
        closingDate,
        chartererOrganizationId,
        senderOrganisationId,
        receiverOrganisationId,
        supplyContractId,
        imoClass,
        incoTerms,
        cargoExclusions,
        cargoExclusionAmount,
        cargoExclusionType,
        specialRequirements,
        orderAttributes,
        loadingOverheadDuration,
        dischargingOverheadDuration,
        ...rest
      } = data;

      if (Object.keys(rest).length > 0) {
        console.error(
          `There are some unprocessed fields on the form: ${JSON.stringify(
            rest,
          )}`,
        );
      }

      if (layday && cancelling && layday > cancelling) {
        toast.error('Loading window end needs to be later than start.');
        return;
      }

      if (rta && deliveredBefore && rta > deliveredBefore) {
        toast.error('Discharge window end needs to be later than start.');
        return;
      }

      if (layday && rta && layday > rta) {
        toast.warning(
          'Discharge window start needs to be later than loading window start. proceed with caution',
        );
        // https://seaber2.myjetbrains.com/youtrack/issue/SEABER-3294
      }
      if (layday && deliveredBefore && layday > deliveredBefore) {
        toast.error(
          'Discharge window end needs to be later than loading window start.',
        );
        return;
      }

      const saveErrors = await onSave(
        {
          order: {
            ...rest,
            id: order?.id ?? null,
            chartererOrganizationId,
            assortmentId,
            cargoAmount: cargoAmount,
            cargoUnitId: cargoUnitId,
            externalOrderId: (externalOrderId ?? '').trim() || null, // An empty string would be considered a unique value and needs to be null instead
            marginAmount: marginAmount,
            marginUnit,
            stowageFactorMin: stowageFactor?.min ?? null,
            stowageFactorMax: stowageFactor?.max ?? null,
            specificGravity,
            layday: layday?.toISOString() ?? null,
            cancelling: cancelling?.toISOString() ?? null,
            rta: rta?.toISOString() ?? null,
            deliveredBefore: deliveredBefore?.toISOString() ?? null,
            loadPortId: loadPort.value,
            dischargePortId: dischargePort.value,
            loadingRate,
            loadingUnit,
            dischargingRate,
            dischargingUnit,
            loadingOverheadDuration:
              hToMs(Number(loadingOverheadDuration)) ?? null,
            dischargingOverheadDuration:
              hToMs(Number(dischargingOverheadDuration)) ?? null,
          },
          orderDetail: {
            id: orderDetail?.id ?? null,
            closingDate: closingDate?.toISOString() ?? null,
            receiverOrganisationId,
            senderOrganisationId,
            supplyContractId,
            incoTerms: asIncotermValue(incoTerms ?? null),
            orderName: orderName ? orderName : null,
            fixtureNote,
            imoClass: imoClass ? (imoClass as ImoClass) : null,
            extraInfo,
          },
          cargoExclusions: cargoExclusions.map((ce) =>
            buildCargoExclusion(
              cargoExclusionType,
              cargoExclusionAmount,
              ce.value,
            ),
          ),
          previousExclusions:
            order?.cargoExclusionsByOrderId.nodes.filter(isDefined) ?? [],
          specialRequirements: specialRequirements.map((sr) => ({
            specialRequirementId: sr.value,
          })),
          previousSpecialRequirements:
            order?.orderRequirementAssociationsByOrderId.nodes.filter(
              isDefined,
            ) ?? [],
          orderAttributes: orderAttributes.map((sr) => ({
            orderAttributeId: sr.value,
          })),
          previousOrderAttributes:
            order?.orderAttributeAssociationsByOrderId.nodes.filter(
              isDefined,
            ) ?? [],
        },
        { shouldClose, createAnother },
      );

      if (saveErrors.length > 0) {
        if (
          saveErrors.some((err) => err.message.includes('Duplicate order name'))
        ) {
          setError('orderName', { message: 'Duplicate order name' });
        }
      }
    };

  const setAssortment = (id: number | null) => {
    if (id === null) {
      return;
    }

    const { cargoExclusions, cargoExclusionAmount, cargoExclusionType } =
      getInitialExclusionValues(id);

    setValue('assortmentId', id);

    const assortment = getAssortmentById(id);
    if (assortment === null) {
      throw new Error(`Assortment not found: ${id}`);
    }

    const {
      cargoUnitId,
      loadingUnit,
      loadingRate,
      dischargingUnit,
      dischargingRate,
      stowageFactorMin,
      stowageFactorMax,
      specificGravity,
      marginAmount,
      marginUnit,
      imoClass,
    } = assortment;

    reset({
      ...getValues(),
      assortmentId: id,
      cargoExclusions,
      cargoExclusionAmount,
      cargoExclusionType,
      stowageFactor: {
        min: stringToNumOpt(stowageFactorMin),
        max: stringToNumOpt(stowageFactorMax),
      },
      specificGravity: stringToNumOpt(specificGravity),
      marginUnit: marginUnit ?? undefined,
      marginAmount: stringToNumOpt(marginAmount),
      imoClass: imoClass ?? undefined,
      loadingUnit: loadingUnit ?? getValues().loadingUnit,
      loadingRate: loadingRate
        ? stringToNumOpt(loadingRate)
        : getValues().loadingRate,
      dischargingUnit: dischargingUnit ?? getValues().dischargingUnit,
      dischargingRate: dischargingRate
        ? stringToNumOpt(dischargingRate)
        : getValues().dischargingRate,
      cargoUnitId: cargoUnitId ?? getValues().cargoUnitId,
    });
  };

  const getPortSpecialRequirements = (
    portId: number | null | undefined,
  ): number[] => {
    if (typeof portId !== 'number') {
      return [];
    } else {
      const specialRequirementPort =
        data.allLocationRequirementAssociations?.nodes
          .filter((lr) => lr?.locationId === portId)
          .map((lr) => lr?.specialRequirementId)
          .filter(isDefined) ?? [];
      const specialRequirementPortZone =
        data.allLocationRequirementAssociations?.nodes
          .flatMap((lr) => lr?.zoneByZoneId?.zoneLocationsByZoneId.nodes)
          .filter((zone) => zone?.locationId === portId)
          .filter(isDefined)
          .map((zl) => zl.locationId) ?? [];
      return unique([...specialRequirementPort, ...specialRequirementPortZone]);
    }
  };

  const setPort = (
    portType: keyof OrderFormFields & ('loadPort' | 'dischargePort'),
    value: LocationSelectValue,
  ) => {
    const otherPortId = portType === 'loadPort' ? dischargePortId : loadPortId;
    const currentRequirements = getPortSpecialRequirements(value?.value);
    const otherPortRequirements = getPortSpecialRequirements(otherPortId);
    const combinedReqs = unique(
      currentRequirements.concat(otherPortRequirements),
    )
      .map((specialRequirementId) =>
        specialRequirementOptions.find(
          (sr) => sr.value === specialRequirementId,
        ),
      )
      .filter(isDefined);

    if (!isEdit) {
      if (portType === 'loadPort') {
        setLoadPortTimezone(value?.location?.timezone ?? undefined);
      } else {
        setDischargePortTimezone(value?.location?.timezone ?? undefined);
      }
    }

    setValue('specialRequirements', combinedReqs);
    setValue(
      portType,
      value?.value
        ? { value: value.value, label: value.label ?? 'Location name N/A' }
        : undefined,
    );
  };

  let sectionCounter = 0;

  return (
    <View height="100%">
      <Form
        style={{ overflowY: 'scroll' }}
        onSubmit={handleSubmit(onSubmit(false, true))}
      >
        <FormSection
          borderBottomStyle="none"
          marginTop={2}
          marginLeft={-2}
          marginBottom={-3}
        >
          <FormTitle>
            <Text fontSize="1.4em" fontWeight="bold">
              {readOnly
                ? 'View order'
                : isEdit
                  ? 'Edit order' + (orderName ? ' ' + orderName : '')
                  : 'New order'}
            </Text>
          </FormTitle>
        </FormSection>
        <FormSection
          muted={++sectionCounter % 2 === 0}
          title="Commodity"
          zIndex={4}
        >
          <Input
            type="hidden"
            {...register(`assortmentId`, { required: true })}
          />
          <FormRow>
            <FormField
              label="Commodity"
              data-testid="order-form-commodity"
              width={250}
              required
              hasError={!!errors.assortmentId}
            >
              <Controller
                control={control}
                name="assortmentId"
                defaultValue={defaultValues.assortmentId}
                render={({ field }) => (
                  <AutoCompleteSelect
                    aria-label="assortment"
                    isDisabled={readOnly}
                    options={assortmentOptions}
                    value={field.value}
                    onChange={(value) => {
                      const newValue = value ?? 0;
                      setAssortment(newValue);
                    }}
                  />
                )}
              />
            </FormField>
            <FormField
              label="Amount"
              data-testid="order-form-amount"
              width={250}
              required
              hasError={!!errors.cargoAmount}
            >
              <Input
                disabled={
                  (readOnly ||
                    order?.loadExternallyPlanned ||
                    order?.dischargeExternallyPlanned) ??
                  false
                }
                {...register('cargoAmount', { setValueAs: stringToNumber })}
                type="number"
                required
              />
            </FormField>
            <FormField label="Unit" width={250} data-testid="order-form-unit">
              <Select
                disabled={readOnly}
                {...register('cargoUnitId', { setValueAs: stringToNumber })}
                options={cargoUnitOptions}
                required
              />
            </FormField>
          </FormRow>

          <Button
            onPress={() => setSettingsOpen(!isSettingsOpen)}
            variant="secondary"
            width="11em"
            marginTop={3}
          >
            Default settings {isSettingsOpen ? UPCARET : DOWNCARET}
          </Button>

          <Box
            borderWidth="1px"
            borderStyle="solid"
            borderColor="input.border"
            padding={4}
            marginTop={3}
            display={isSettingsOpen ? undefined : 'none'}
          >
            <FormRow>
              <FormField
                label="Stowage factor MIN (ft³&#8202;/&#8202;LT)"
                data-testid="order-form-stowage-factor-min"
                width={250}
                hasError={!!errors.stowageFactor?.min}
              >
                <Input
                  disabled={readOnly}
                  {...register('stowageFactor.min', {
                    setValueAs: stringToNumber,
                  })}
                  type="number"
                  placeholder={assortment?.stowageFactorMin?.toString()}
                />
              </FormField>
              <FormField
                label="Stowage factor MAX (ft³&#8202;/&#8202;LT)"
                data-testid="order-form-stowage-factor-max"
                width={250}
                hasError={!!errors.stowageFactor?.max}
              >
                <Input
                  disabled={readOnly}
                  {...register('stowageFactor.max', {
                    setValueAs: stringToNumber,
                  })}
                  type="number"
                  placeholder={assortment?.stowageFactorMax?.toString()}
                />
              </FormField>
              <FormField
                label="Specific gravity"
                data-testid="order-form-specific-gravity"
                width={250}
                hasError={!!errors.specificGravity}
              >
                <Input
                  disabled={readOnly}
                  {...register('specificGravity', {
                    setValueAs: stringToNumber,
                  })}
                  type="number"
                  step="any"
                  placeholder={assortment?.specificGravity?.toString()}
                />
              </FormField>
            </FormRow>

            <FormRow>
              <FormField
                label="Margin"
                data-testid="order-form-margin"
                width={250}
                hasError={!!errors.marginAmount}
              >
                <Input
                  disabled={readOnly}
                  {...register('marginAmount', {
                    setValueAs: stringToNumber,
                  })}
                  type="number"
                />
              </FormField>
              <FormField
                label="Margin unit"
                data-testid="order-form-margin-unit"
                width={250}
                hasError={!!errors.marginUnit}
              >
                <Select
                  disabled={readOnly}
                  {...register('marginUnit')}
                  options={marginUnits}
                />
              </FormField>

              <FormField
                label="IMO class"
                width={250}
                data-testid="order-form-imo-class"
              >
                <Select
                  disabled={readOnly}
                  {...register('imoClass')}
                  options={imoClassOptions}
                  placeholder="Select IMO class"
                  required
                />
              </FormField>
            </FormRow>

            <FormRow marginTop={3} marginBottom={3}>
              <CalculationBox>
                <Box display="flex" flexDirection="row" flexGrow={1}>
                  <Box display="flex" flexDirection="column" flexGrow={1}>
                    <div>Min volume</div>
                    <div>{cargoRanges.volume.min.toFixed(2)} m³</div>
                  </Box>
                  <Box display="flex" flexDirection="column" flexGrow={1}>
                    <div>Max volume</div>
                    <div>{cargoRanges.volume.max.toFixed(2)} m³</div>
                  </Box>
                </Box>
              </CalculationBox>

              <Box width="1em" />

              <CalculationBox>
                <Box display="flex" flexDirection="row" flexGrow={1}>
                  <Box display="flex" flexDirection="column" flexGrow={1}>
                    <div>Min mass</div>
                    <div>{cargoRanges.mass.min.toFixed(2)} mts</div>
                  </Box>
                  <Box display="flex" flexDirection="column" flexGrow={1}>
                    <div>Max mass</div>
                    <div>{cargoRanges.mass.max.toFixed(2)} mts</div>
                  </Box>
                </Box>
              </CalculationBox>
            </FormRow>

            <FormRow>
              <FormField
                label="Previous cargo exclusions"
                width={250}
                hasError={!!errors.cargoExclusions}
              >
                <Controller
                  control={control}
                  name="cargoExclusions"
                  defaultValue={[]}
                  render={({ field: { onChange, value } }) => (
                    <ReactSelect
                      isDisabled={readOnly}
                      name="cargoExclusions"
                      options={cargoExclusionOptions}
                      placeholder="Select cargo exclusions"
                      isMulti
                      value={value?.map(
                        (sel: (typeof cargoExclusionOptions)[0]) =>
                          cargoExclusionOptions.find(
                            (eo) => eo.value === sel.value,
                          ),
                      )}
                      onChange={(value) => onChange(Number(value) || [])}
                      styles={reactSelectStyles}
                    />
                  )}
                />
              </FormField>
              <FormField
                label="Exclusion required for"
                width={250}
                required
                display={
                  !currentExclusions || currentExclusions.length === 0
                    ? 'none'
                    : undefined
                }
                hasError={!!errors.cargoExclusionAmount}
              >
                <Input
                  disabled={readOnly}
                  {...register('cargoExclusionAmount')}
                  type="number"
                  required
                />
              </FormField>

              <FormField
                label="Exclusion amount type"
                display={
                  !currentExclusions || currentExclusions.length === 0
                    ? 'none'
                    : undefined
                }
                width={250}
                required
              >
                <Select
                  disabled={readOnly}
                  {...register('cargoExclusionType')}
                  options={cargoExclusionTypeOptions}
                  placeholder="Select cargo exclusion type"
                  required
                />
              </FormField>
            </FormRow>
          </Box>
        </FormSection>

        <FormSection
          muted={++sectionCounter % 2 === 0}
          title="Ports"
          zIndex={3}
        >
          <MissingPortCoordinatesError order={order} />
          <InvalidDataErrors order={order} />
          <FormRow>
            <FormField
              label="Load port"
              data-testid="order-form-load-port"
              width={250}
              required
              title={cannotChangePortReason}
              hasError={!!errors.loadPort}
            >
              <Controller
                defaultValue={undefined}
                control={control}
                name="loadPort"
                render={({ field: { value } }) => (
                  <LocationSelect
                    value={value}
                    aria-label="loadPort"
                    isDisabled={!!cannotChangePortReason || readOnly}
                    onChange={(v: LocationSelectValue) => {
                      if (
                        isEdit &&
                        order?.orderPortCallRelationsByOrderId.nodes.some(
                          (opcr) => opcr?.isLoadPort,
                        )
                      ) {
                        setConfirmChangePort({
                          description:
                            'Are you sure you want to change the load port? This change will affect all scenarios and users immediately after saving. Changing ports for scheduled orders will also clear any added port documents or information.',
                          handler: () => setPort('loadPort', v),
                        });
                        return;
                      }
                      setPort('loadPort', v);
                    }}
                  />
                )}
                rules={{ required: true }}
              />
            </FormField>
            <FormField
              label="Discharge port"
              data-testid="order-form-discharge-port"
              width={250}
              required
              title={cannotChangePortReason}
              hasError={!!errors.dischargePort}
            >
              <Controller
                defaultValue={undefined}
                control={control}
                name="dischargePort"
                render={({ field: { value } }) => (
                  <LocationSelect
                    value={value}
                    aria-label="dischargePort"
                    isDisabled={!!cannotChangePortReason || readOnly}
                    onChange={(v: LocationSelectValue) => {
                      if (
                        isEdit &&
                        order?.orderPortCallRelationsByOrderId.nodes.some(
                          (opcr) => opcr?.isLoadPort === false,
                        )
                      ) {
                        setConfirmChangePort({
                          description:
                            'Are you sure you want to change the discharge port? This change will affect all scenarios and users immediately after saving. Changing ports for scheduled orders will also clear any added port documents or information.',
                          handler: () => setPort('dischargePort', v),
                        });
                        return;
                      }
                      setPort('dischargePort', v);
                    }}
                  />
                )}
                rules={{ required: true }}
              />
            </FormField>
          </FormRow>

          <FormRow>
            <FormField
              label="Closing date"
              data-testid="order-form-closing-date"
              width={250}
              hasError={!!errors.closingDate}
            >
              <Controller
                defaultValue={null}
                control={control}
                name="closingDate"
                render={({ field: { value, onChange } }) => (
                  <DatePicker
                    disabled={readOnly}
                    showTimeInput
                    onChange={onChange}
                    selected={value}
                    timeZone={loadPortTimezone}
                  />
                )}
              />
            </FormField>

            <FormField
              label="Earliest loading"
              data-testid="order-form-earliest-loading"
              width={250}
              hasError={!!errors.layday}
              notifications={
                hasLaydayOverride && voyageCharterLaydayMs ? (
                  <OverrideTooltip
                    message={`Current value overrides Layday set on Voyage Charter. Overridden value: ${timeFieldFormat(voyageCharterLaydayMs, loadPortTimezone)}`}
                  />
                ) : null
              }
            >
              <Controller
                defaultValue={null}
                control={control}
                name="layday"
                render={({ field: { value, onChange } }) => (
                  <DatePicker
                    disabled={
                      (readOnly || order?.loadExternallyPlanned) ?? false
                    }
                    showTimeInput
                    onChange={onChange}
                    selected={value}
                    timeZone={loadPortTimezone}
                  />
                )}
              />
            </FormField>
            <FormField
              label="Latest loading"
              data-testid="order-form-latest-loading"
              width={250}
              hasError={!!errors.cancelling}
              notifications={
                hasCancellingOverride && voyageCharterCancellingMs ? (
                  <OverrideTooltip
                    message={`Current value overrides Layday set on Voyage Charter. Overridden value: ${timeFieldFormat(voyageCharterCancellingMs, loadPortTimezone)}`}
                  />
                ) : null
              }
            >
              <Controller
                defaultValue={null}
                control={control}
                name="cancelling"
                render={({ field: { value, onChange } }) => (
                  <DatePicker
                    disabled={
                      (readOnly || order?.loadExternallyPlanned) ?? false
                    }
                    showTimeInput
                    onChange={onChange}
                    selected={value}
                    defaultTimePickerType="END_OF_DAY"
                    timeZone={loadPortTimezone}
                  />
                )}
              />
            </FormField>

            <FormField
              label="Earliest discharging"
              data-testid="order-form-earliest-discharging"
              width={250}
              hasError={!!errors.rta}
            >
              <Controller
                defaultValue={null}
                control={control}
                name="rta"
                render={({ field: { value, onChange } }) => (
                  <DatePicker
                    disabled={
                      (readOnly || order?.dischargeExternallyPlanned) ?? false
                    }
                    showTimeInput
                    onChange={onChange}
                    selected={value}
                    timeZone={dischargePortTimezone}
                  />
                )}
              />
            </FormField>

            <FormField
              label="Latest discharging"
              data-testid="order-form-latest-discharging"
              width={250}
              hasError={!!errors.deliveredBefore}
            >
              <Controller
                defaultValue={null}
                control={control}
                name="deliveredBefore"
                render={({ field: { value, onChange } }) => (
                  <DatePicker
                    disabled={
                      (readOnly || order?.dischargeExternallyPlanned) ?? false
                    }
                    showTimeInput
                    onChange={onChange}
                    selected={value}
                    defaultTimePickerType="END_OF_DAY"
                    timeZone={dischargePortTimezone}
                  />
                )}
              />
            </FormField>
          </FormRow>

          <FormRow>
            <FormField
              label="Loading rate"
              data-testid="order-form-loading-rate"
              width={250}
              hasError={!!errors.loadingRate}
              notifications={
                hasLoadingRateOverride ? (
                  <OverrideTooltip
                    message={formatLoadingRateOverrideTooltip(
                      effectiveLoadingRateParams,
                    )}
                  />
                ) : null
              }
            >
              <Input
                disabled={readOnly}
                {...register('loadingRate', { setValueAs: stringToNumber })}
                type="number"
                placeholder={loadingRatePlaceholder}
              />
            </FormField>
            <FormField
              label="Loading unit"
              data-testid="order-form-loading-unit"
              width={250}
              hasError={!!errors.loadingUnit}
            >
              <Select
                disabled={readOnly}
                {...register('loadingUnit', { setValueAs: valueOrNull })}
                options={loadingUnitOptions(shouldUseReducedUnits)}
                placeholder="-"
                enablePlaceholder
              />
            </FormField>

            <FormField
              label="Discharging rate"
              data-testid="order-form-discharging-rate"
              width={250}
              hasError={!!errors.dischargingRate}
              notifications={
                hasDischargeRateOverride ? (
                  <OverrideTooltip
                    message={formatLoadingRateOverrideTooltip(
                      effectiveDischargeRateParams,
                    )}
                  />
                ) : null
              }
            >
              <Input
                disabled={readOnly}
                {...register('dischargingRate', {
                  setValueAs: stringToNumber,
                })}
                type="number"
                placeholder={dischargeRatePlaceholder}
              />
            </FormField>
            <FormField
              label="Discharging unit"
              data-testid="order-form-discharging-unit"
              width={250}
              hasError={!!errors.dischargingUnit}
            >
              <Select
                disabled={readOnly}
                {...register('dischargingUnit', { setValueAs: valueOrNull })}
                options={loadingUnitOptions(shouldUseReducedUnits)}
                placeholder="-"
                enablePlaceholder
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField
              label="Adjustments for loading (hrs)"
              data-testid="order-form-adjustments-for-loading"
              width={250}
              hasError={!!errors.loadingOverheadDuration}
            >
              <Input
                disabled={readOnly}
                {...register('loadingOverheadDuration', {
                  setValueAs: stringToNumber,
                })}
                type="number"
                step="0.01"
              />
            </FormField>
            <FormField
              label="Adjustments for discharging (hrs)"
              data-testid="order-form-adjustments-for-discharging"
              width={250}
              hasError={!!errors.dischargingOverheadDuration}
            >
              <Input
                disabled={readOnly}
                {...register('dischargingOverheadDuration', {
                  setValueAs: stringToNumber,
                })}
                type="number"
                step="0.01"
              />
            </FormField>
          </FormRow>
          <FormRow marginTop={3} marginBottom={3}>
            {!loadingRate && !dischargingRate && (
              <CalculationBox>
                <div>
                  Loading and discharging rates can be defined in COA or spot
                  contracts
                </div>
              </CalculationBox>
            )}

            {(minLoadingTime > 0 || maxLoadingTime > 0) && (
              <>
                <CalculationBox>
                  <Box display="flex" flexDirection="row" flexGrow={1}>
                    <Box display="flex" flexDirection="column" flexGrow={1}>
                      <div>Min loading time</div>
                      <div>{minLoadingTime.toFixed(1)} h</div>
                    </Box>
                    <Box display="flex" flexDirection="column" flexGrow={1}>
                      <div>Max loading time</div>
                      <div>{maxLoadingTime.toFixed(1)} h</div>
                    </Box>
                  </Box>
                </CalculationBox>

                <Box width="1em" />
              </>
            )}

            {(minDischargingTime > 0 || maxDischargingTime > 0) && (
              <CalculationBox>
                <Box display="flex" flexDirection="row" flexGrow={1}>
                  <Box display="flex" flexDirection="column" flexGrow={1}>
                    <div>Min discharging time</div>
                    <div>{minDischargingTime.toFixed(1)} h</div>
                  </Box>
                  <Box display="flex" flexDirection="column" flexGrow={1}>
                    <div>Max discharging time</div>
                    <div>{maxDischargingTime.toFixed(1)} h</div>
                  </Box>
                </Box>
              </CalculationBox>
            )}
          </FormRow>
        </FormSection>

        <FormSection
          title="Restrictions and attributes"
          muted={++sectionCounter % 2 === 0}
          zIndex={2}
        >
          <FormRow>
            <FormField
              label="Special requirements"
              data-testid="order-form-special-requirements"
              width={250}
              hasError={!!errors.specialRequirements}
            >
              <Controller
                control={control}
                name="specialRequirements"
                render={({ field: { value, onChange } }) => (
                  <ReactSelect
                    isDisabled={readOnly}
                    options={specialRequirementOptions}
                    placeholder="Select special requirements"
                    isMulti
                    value={value?.map(
                      (sel: (typeof specialRequirementOptions)[0]) =>
                        specialRequirementOptions.find(
                          (sr) => sr.value === sel.value,
                        ),
                    )}
                    onChange={(value) => onChange(value || [])}
                    styles={restrictionsAttributesSelectStyles}
                  />
                )}
              />
            </FormField>

            <FormField
              label="Attributes"
              data-testid="order-form-attributes"
              width={250}
              hasError={!!errors.orderAttributes}
            >
              <Controller
                control={control}
                name="orderAttributes"
                render={({ field: { value, onChange } }) => (
                  <ReactSelect
                    isDisabled={readOnly}
                    options={orderAttributeOptions}
                    placeholder="Select attributes"
                    isMulti
                    value={value?.map(
                      (sel: (typeof orderAttributeOptions)[0]) =>
                        orderAttributeOptions.find(
                          (sr) => sr.value === sel.value,
                        ),
                    )}
                    onChange={(value) => onChange(value || [])}
                    styles={restrictionsAttributesSelectStyles}
                  />
                )}
              />
            </FormField>
          </FormRow>
        </FormSection>

        <FormSection
          muted={++sectionCounter % 2 === 0}
          title="Other details"
          zIndex={1}
        >
          {supplyContractOptions.length > 0 && (
            <FormRow>
              <FormField
                label="Supply contract"
                data-testid="order-form-supply-contract"
                width={250}
                hasError={!!errors.supplyContractId}
              >
                <Controller
                  control={control}
                  name="supplyContractId"
                  defaultValue={null}
                  render={({ field: { value } }) => (
                    <AutoCompleteSelect
                      isDisabled={readOnly}
                      aria-label="Select supply contract"
                      options={supplyContractOptions}
                      value={value}
                      onChange={(newValue) =>
                        setValue('supplyContractId', newValue)
                      }
                      maxHeight={150}
                    />
                  )}
                />
              </FormField>
            </FormRow>
          )}
          <FormRow>
            <FormField
              label="Charterer"
              data-testid="order-form-charterer"
              width={250}
              hasError={!!errors.chartererOrganizationId}
            >
              <Controller
                control={control}
                name="chartererOrganizationId"
                defaultValue={null}
                render={({ field: { value } }) => (
                  <AutoCompleteSelect
                    isDisabled={readOnly}
                    aria-label="Select charterer organization"
                    options={chartererOrganizationOptions}
                    value={value}
                    onChange={(newValue) =>
                      setValue('chartererOrganizationId', newValue)
                    }
                    maxHeight={150}
                  />
                )}
              />
            </FormField>
            <FormField
              label="Sender"
              data-testid="order-form-sender"
              width={250}
              hasError={!!errors.senderOrganisationId}
            >
              <Controller
                control={control}
                name="senderOrganisationId"
                defaultValue={null}
                render={({ field: { value } }) => (
                  <AutoCompleteSelect
                    isDisabled={readOnly}
                    aria-label="Select organization"
                    options={senderOrganizationOptions}
                    value={value}
                    onChange={(newValue) =>
                      setValue('senderOrganisationId', newValue)
                    }
                    maxHeight={150}
                  />
                )}
              />
            </FormField>
            <FormField
              label="Receiver"
              data-testid="order-form-receiver"
              width={250}
              hasError={!!errors.receiverOrganisationId}
            >
              <Controller
                control={control}
                name="receiverOrganisationId"
                defaultValue={null}
                render={({ field: { value } }) => (
                  <AutoCompleteSelect
                    isDisabled={readOnly}
                    aria-label="Select organization"
                    options={receiverOrganizationOptions}
                    value={value}
                    onChange={(newValue) =>
                      setValue('receiverOrganisationId', newValue)
                    }
                    maxHeight={150}
                  />
                )}
              />
            </FormField>

            <FormField
              label="Incoterms"
              data-testid="order-form-incoterms"
              width={250}
              hasError={!!errors.incoTerms}
            >
              <Controller
                control={control}
                name="incoTerms"
                defaultValue={null}
                render={({ field: { value } }) => (
                  <AutoCompleteSelect
                    isDisabled={readOnly}
                    aria-label="Select inco terms"
                    options={incoTermOptions}
                    value={value}
                    onChange={(newValue) => setValue('incoTerms', newValue)}
                    maxHeight={150}
                  />
                )}
              />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField
              label="Order Name"
              data-testid="order-form-order-name"
              width={250}
              hasError={!!errors.orderName}
            >
              <Input disabled={readOnly} {...register('orderName')} />
            </FormField>
            <FormField
              label="External system ID"
              data-testid="order-form-external-system-ID"
              width={250}
              hasError={!!errors.externalOrderId}
            >
              <Input disabled={true} {...register('externalOrderId')} />
            </FormField>
            <FormField
              label="Fixture note"
              data-testid="order-form-fixture-note"
              width={250}
              hasError={!!errors.fixtureNote}
            >
              <Input disabled={true} {...register('fixtureNote')} />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField
              label="Other remarks"
              data-testid="order-form-other-remarks"
              width={510}
              hasError={!!errors.extraInfo}
            >
              <TextArea
                rows={4}
                disabled={readOnly}
                {...register('extraInfo')}
              ></TextArea>
            </FormField>
          </FormRow>
        </FormSection>

        {errorMessages.length > 0 && (
          <FormSection muted={sectionCounter % 2 === 1}>
            <Text color="#9d0300">{errorMessages.join('; ')}</Text>
          </FormSection>
        )}
      </Form>
      {!readOnly && (
        <ModalFooter>
          {onCancel && (
            <Button
              variant="secondary"
              disabled={isLoading}
              onPress={onCancel}
              marginRight="3"
            >
              Discard changes & Close
            </Button>
          )}
          {onDelete && (
            <>
              <Button
                variant="negative"
                disabled={isLoading || isAssigned}
                onPress={() => setConfirmDeleteOpen(true)}
                marginRight="3"
              >
                Delete order
              </Button>
              {isAssigned && (
                <Text color="#eee" mr={3}>
                  Only open orders can be deleted
                </Text>
              )}
            </>
          )}
          {!isEdit && (
            <Button
              variant="secondary"
              disabled={isLoading}
              onPress={handleSubmit(onSubmit(true, true))}
              marginRight="3"
            >
              Save & Add another
            </Button>
          )}
          <Button
            aria-label="save"
            disabled={isLoading}
            onPress={handleSubmit(onSubmit(false, false))}
            mr={3}
          >
            {'Save'}
          </Button>
          {isLoading && (
            <View flexDirection="row" mr={3}>
              <Text color="white" mr={3}>
                Saving changes
              </Text>
              <LoadingSpinner variant="light" />
            </View>
          )}
          <Button
            aria-label="save and close"
            disabled={isLoading}
            onPress={handleSubmit(onSubmit(false, true))}
          >
            Save & Close
          </Button>
          <div style={{ flexGrow: 1 }} />
        </ModalFooter>
      )}
      {confirmChangePort !== null && (
        <Modal
          isOpen={true}
          onClose={() => setConfirmChangePort(null)}
          width="568px"
          height="260px"
        >
          <ModalBody>
            <Text fontWeight="bold">{confirmChangePort.description}</Text>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="secondary"
              mr="2"
              onPress={() => setConfirmChangePort(null)}
            >
              Cancel
            </Button>
            <Button
              variant="negative"
              onPress={() => {
                confirmChangePort.handler();
                setConfirmChangePort(null);
              }}
            >
              Change
            </Button>
          </ModalFooter>
        </Modal>
      )}
      {onDelete && isConfirmDeleteOpen && (
        <Modal
          isOpen={true}
          onClose={() => setConfirmDeleteOpen(false)}
          width="568px"
          height="260px"
        >
          <ModalBody>
            <Text fontWeight="bold">
              Are you sure you want to delete this order?
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="secondary"
              mr="2"
              onPress={() => setConfirmDeleteOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="negative" disabled={isLoading} onPress={onDelete}>
              Delete
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </View>
  );
};

export default OrderForm;
